import {
  Bike,
  Bird,
  Bot,
  Building2,
  CalendarClock,
  Container,
  CreditCard,
  Laptop,
  LayoutDashboard,
  MessagesSquare,
  MonitorPlay,
  Plug,
  ShoppingBag,
  Smartphone,
  Store,
  TabletSmartphone,
  Zap,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { CATALOGO_DE_PRODUCTOS, fondoDePortada, portadaDeProducto } from './productoVisual'

/**
 * Nombre de lucide → componente.
 *
 * 🚨 **Esto NO es una lista de productos** —esa es `catalogo-de-productos.json`
 * y es la única—: es la resolución técnica del nombre que el catálogo declara,
 * que hace falta porque importar lucide entero para buscar por nombre metería
 * los miles de iconos de la librería en el bundle.
 *
 * Un icono del catálogo que no esté aquí **pone rojo** el candado de
 * `PortadaProducto.test.tsx`: el producto se pintaría sin icono y con su fondo,
 * que es un hueco raro y no el caso por defecto.
 */
const ICONO_POR_NOMBRE: Record<string, LucideIcon> = {
  Bike,
  Bird,
  Bot,
  Building2,
  CalendarClock,
  Container,
  CreditCard,
  Laptop,
  LayoutDashboard,
  MessagesSquare,
  MonitorPlay,
  Plug,
  ShoppingBag,
  Smartphone,
  Store,
  TabletSmartphone,
  Zap,
}

export interface PortadaProductoProps {
  /** Rótulo del producto tal como lo manda el servidor (texto libre). */
  product: string
  /**
   * `ficha`: cuadrada y compacta, para la fila del aviso — el rótulo va AL
   * LADO como texto, así que la portada se marca decorativa.
   * `ancha`: banda con el rótulo DENTRO, para la página propia.
   */
  medida?: 'ficha' | 'ancha'
  className?: string
}

/**
 * La portada de un producto, **pintada** (goal novedades-del-portal 2,
 * decisión 5 de Víctor): fondo compuesto + icono, sin subir un fichero ni
 * depender de Apolo.
 *
 * 🚨 El rótulo NUNCA se hornea en el fondo: en `ancha` es un `<span>` de
 * verdad —traducible y leíble por un lector de pantalla— y en `ficha` vive
 * fuera. Un texto rasterizado no se puede traducir, ni leer, ni buscar.
 *
 * `backgroundColor` va aparte del `backgroundImage` a propósito: es la red de
 * seguridad si el navegador no entiende `oklch()`. Sin ella, un degradado que
 * no parsea deja un cuadro transparente con un icono blanco encima.
 */
export function PortadaProducto({
  product,
  medida = 'ficha',
  className,
}: PortadaProductoProps) {
  const { clave, hue, inicial } = portadaDeProducto(product)
  const Icono = clave
    ? (ICONO_POR_NOMBRE[CATALOGO_DE_PRODUCTOS[clave].icono] ?? null)
    : null
  const estilo = {
    backgroundColor: '#55606f',
    backgroundImage: fondoDePortada(hue),
  }

  if (medida === 'ancha') {
    return (
      <span
        className={cn(
          'flex h-[92px] w-full items-center gap-3 rounded-xl px-[18px] text-white',
          'shadow-[inset_0_1px_0_rgba(255,255,255,0.28)]',
          className,
        )}
        style={estilo}
        data-testid="portada-producto"
      >
        {Icono ? (
          <Icono className="h-[26px] w-[26px] shrink-0" aria-hidden="true" />
        ) : (
          <span
            className="text-2xl font-extrabold leading-none"
            aria-hidden="true"
          >
            {inicial}
          </span>
        )}
        <span className="text-[13px] font-bold tracking-wide [text-shadow:0_1px_2px_rgba(0,0,0,0.22)]">
          {product}
        </span>
      </span>
    )
  }

  return (
    <span
      className={cn(
        'grid h-[58px] w-[58px] shrink-0 place-items-center rounded-[13px] text-white',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.28)]',
        className,
      )}
      style={estilo}
      aria-hidden="true"
      data-testid="portada-producto"
    >
      {Icono ? (
        <Icono className="h-[25px] w-[25px]" />
      ) : (
        <span className="text-[22px] font-extrabold leading-none">
          {inicial}
        </span>
      )}
    </span>
  )
}
